import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

const empty = { title: "", description: "", start_date: "", end_date: "", type: "activity", location: "" };
const TYPES = { campaign: "Campanya", fair: "Fira", training: "Formació", meeting: "Reunió", activity: "Activitat" };

export default function AdminEvents() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [selected, setSelected] = useState(new Date());

  const load = async () => setItems((await api.get("/events")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/events", form);
      toast.success("Creat"); setOpen(false); setForm(empty); load();
    } catch { toast.error("Error"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Segur?")) return;
    await api.delete(`/events/${id}`); load();
  };

  const eventDates = items.map(i => new Date(i.start_date));

  return (
    <div className="space-y-6" data-testid="admin-events">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline text-accent">Calendari</div>
          <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Agenda</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="event-new-btn"><Plus className="w-4 h-4 mr-2" /> Nou event</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nou event</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Títol</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-form-title" /></div>
              <div><Label>Descripció</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Inici</Label><Input type="datetime-local" value={form.start_date?.slice(0, 16)} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                <div><Label>Fi</Label><Input type="datetime-local" value={form.end_date?.slice(0, 16)} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tipus</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Ubicació</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="event-form-save">Guardar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="rounded-2xl border-2 lg:col-span-1">
          <CardContent className="p-4">
            <Calendar mode="single" selected={selected} onSelect={setSelected} modifiers={{ event: eventDates }} modifiersClassNames={{ event: "bg-accent/30 rounded-full font-bold" }} />
          </CardContent>
        </Card>
        <div className="lg:col-span-2 space-y-3">
          {items.map((e) => (
            <Card key={e.id} className="rounded-2xl border-2" data-testid={`event-card-${e.id}`}>
              <CardContent className="p-5 flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><CalendarDays className="w-5 h-5" /></div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-accent font-semibold">{TYPES[e.type] || e.type}</div>
                  <div className="font-heading font-semibold text-lg mt-1">{e.title}</div>
                  <div className="text-sm text-muted-foreground">{e.description}</div>
                  <div className="text-xs text-muted-foreground mt-2">
                    {new Date(e.start_date).toLocaleString()} · {e.location}
                  </div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => remove(e.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </CardContent>
            </Card>
          ))}
          {items.length === 0 && <div className="text-muted-foreground text-sm">Sense events.</div>}
        </div>
      </div>
    </div>
  );
}
